import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Clock, UtensilsCrossed, Phone, Mail, ExternalLink } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { heroText } from "@/lib/pageHero";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { PageContentBlocks } from "@/components/site/PageContentBlocks";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";
import { SectionShell } from "@/components/site/edit/SectionShell";

const pick = (obj, lang) => (obj && (obj[lang] || obj.en)) || "";

export default function KosherEstablishmentsPage() {
  const { t, lang } = useLang();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const { data = { intro: {}, restaurants: [] } } = useQuery({
    queryKey: ["kosher-establishments"],
    queryFn: async () => (await api.get("/kosher-establishments")).data,
  });
  const { data: settings = {} } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const ht = heroText(settings, "kosher-establishments", lang, t, "kosher_estab");
  const restaurants = data.restaurants || [];

  return (
    <div className="relative bg-[#F9F8F6] min-h-screen overflow-x-hidden" data-testid="kosher-establishments-page">
      <div className="grain" />
      <Navbar />

      <PageHero image={data.header_image} eyebrow={ht("eyebrow")} title={ht("title")} description={ht("desc")} icon={UtensilsCrossed} testid="kosher-estab-hero" />

      <PageContentBlocks page="kosher-establishments" position="top" />

      <SectionShell id="kosher-establishments-sec-1" label="Section 1" noReorder>
      <section className="pt-14 sm:pt-16 pb-8">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <p className="text-lg text-[#475569] leading-relaxed whitespace-pre-line">{pick(data.intro, lang)}</p>
            {(data.phone || data.email) && (
              <div className="mt-6 flex flex-wrap gap-3" data-testid="kosher-contact-strip">
                {data.phone && (
                  <a href={`tel:${data.phone.replace(/\s+/g, "")}`} data-testid="kosher-reserve-phone"
                    className="inline-flex items-center gap-2.5 rounded-2xl bg-white border border-[#0F172A]/8 px-4 py-3 shadow-[0_6px_20px_rgba(15,23,42,0.05)] hover:border-[#BE923C] transition-colors">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#BE923C]/12 text-[#BE923C]"><Phone size={16} /></span>
                    <span className="text-start"><span className="block text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]"><EditableText k="kosher_estab.reserve" as="span" /></span><span className="text-sm font-semibold text-[#0F172A]">{data.phone}</span></span>
                  </a>
                )}
                {data.email && (
                  <a href={`mailto:${data.email}`} data-testid="kosher-group-email"
                    className="inline-flex items-center gap-2.5 rounded-2xl bg-white border border-[#0F172A]/8 px-4 py-3 shadow-[0_6px_20px_rgba(15,23,42,0.05)] hover:border-[#BE923C] transition-colors">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#BE923C]/12 text-[#BE923C]"><Mail size={16} /></span>
                    <span className="text-start"><span className="block text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]"><EditableText k="kosher_estab.group" as="span" /></span><span className="text-sm font-semibold text-[#0F172A]">{data.email}</span></span>
                  </a>
                )}
              </div>
            )}
          </Reveal>
        </div>
      </section>
      </SectionShell>

      <SectionShell id="kosher-establishments-sec-2" label="Section 2" noReorder>
      <section className="pb-24 sm:pb-28">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 space-y-6">
          {restaurants.map((r, i) => (
            <Reveal key={r.id || i} delay={(i % 3) * 0.06}>
              <div data-testid={`restaurant-${r.id || i}`} className="grid sm:grid-cols-[240px_1fr] gap-0 overflow-hidden rounded-3xl bg-white border border-[#0F172A]/5 shadow-[0_12px_40px_rgba(15,23,42,0.07)]">
                <div className="relative h-52 sm:h-full min-h-[180px] bg-[#0F172A]/5">
                  {r.image_url
                    ? <img src={resolveImg(r.image_url)} alt={r.name} className="absolute inset-0 w-full h-full object-cover" />
                    : <div className="absolute inset-0 flex items-center justify-center text-[#BE923C]"><UtensilsCrossed size={40} /></div>}
                </div>
                <div className="p-6 sm:p-7">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="font-serif text-2xl text-[#0F172A]">{r.name}</h2>
                    {r.tag && <span className="rounded-full bg-[#BE923C]/12 text-[#9E7829] border border-[#BE923C]/30 px-3 py-0.5 text-xs font-bold uppercase tracking-wide">{r.tag}</span>}
                  </div>
                  {r.description && <p className="mt-2.5 text-[#475569] leading-relaxed">{r.description}</p>}
                  <div className="mt-4 space-y-2 text-sm">
                    {r.address && (
                      <div className="flex items-start gap-2.5 text-[#334155]">
                        <MapPin size={16} className="mt-0.5 shrink-0 text-[#BE923C]" />
                        <span><span className="font-semibold">{t("kosher_estab.address")}: </span>{r.address}</span>
                      </div>
                    )}
                    {r.hours && (
                      <div className="flex items-start gap-2.5 text-[#334155]">
                        <Clock size={16} className="mt-0.5 shrink-0 text-[#BE923C]" />
                        <span className="whitespace-pre-line"><span className="font-semibold">{t("kosher_estab.hours")}: </span>{r.hours}</span>
                      </div>
                    )}
                  </div>
                  {r.map_url && (
                    <a href={r.map_url} target="_blank" rel="noopener noreferrer" data-testid={`restaurant-map-${r.id || i}`}
                      className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#BE923C]/40 text-[#9E7829] hover:bg-[#FBF7EE] px-4 py-2 text-sm font-semibold transition-colors">
                      <MapPin size={14} /> <EditableText k="kosher_estab.map" as="span" /> <ExternalLink size={13} />
                    </a>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      </SectionShell>

      <PageContentBlocks page="kosher-establishments" position="bottom" />

      <Footer />
    </div>
  );
}
